import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Spin, Tooltip, message, Input, Alert } from "antd";
import { useTranslation } from "react-i18next";
import { DownloadOutlined } from "@ant-design/icons";
import { Braces, PanelLeft, Sparkles, Wand2 } from "lucide-react";
import { downloadOrgLlmPromptsCsv } from "pages/Welcome/downloadOrgLlmPromptsCsv";
import firebase from "FirebaseConfig.js";
import { apiRequest } from "util/services";
import { decodeBasicHtmlEntities } from "pages/Welcome";
import { getPromptVariableGroupsForMetric } from "pages/Welcome/promptVariables";
import { PromptUnknownTokensNote } from "pages/Welcome/PromptUnknownTokensNote";
import { PromptMapReduceAccordion } from "components/WelcomePage/PromptMapReduceAccordion";
import welcomeStyles from "pages/Welcome/style.module.scss";

function formatNameForPromptModalChrome(raw) {
  let s = String(raw ?? "").trim();
  if (!s) return "";
  let prev;
  do {
    prev = s;
    s = decodeBasicHtmlEntities(s);
  } while (s !== prev);
  return s.replace(/\s*&\s*/g, " · ");
}

/**
 * Same org-level LLM prompt editor as My Workspace (Welcome), for reuse on AI report widgets.
 */
export function WelcomeOrgPromptEditorModal({ open, onClose, widget }) {
  const { t } = useTranslation();

  const uid = firebase.auth().currentUser?.uid;

  const [promptLoading, setPromptLoading] = useState(false);
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptModeTab, setPromptModeTab] = useState("small");
  const [promptShort, setPromptShort] = useState("");
  const [promptLongMap, setPromptLongMap] = useState("");
  const [promptLongReduce, setPromptLongReduce] = useState("");
  const [canEditOrgPrompts, setCanEditOrgPrompts] = useState(false);
  const promptShortRef = useRef(null);
  const promptMapRef = useRef(null);
  const promptReduceRef = useRef(null);

  useEffect(() => {
    if (!open || !widget?.metric_id) return;

    const load = async () => {
      setPromptLoading(true);
      setCanEditOrgPrompts(false);
      setPromptModeTab("small");
      setPromptShort("");
      setPromptLongMap("");
      setPromptLongReduce("");
      try {
        const mid = widget.metric_id;
        const params = new URLSearchParams();
        if (uid != null && uid !== "") {
          params.set("user_id", uid);
        }
        if (mid != null && mid !== "") {
          params.set("welcome_metric_id", String(mid));
        }
        const qs = params.toString();
        const llmPromptsUrl = qs
          ? `welcome/organization-llm-prompts?${qs}`
          : "welcome/organization-llm-prompts";
        const res = await apiRequest(llmPromptsUrl, "GET", undefined, true);
        setCanEditOrgPrompts(res?.can_edit_organization_llm_prompts === true);
        const rows = res?.success;
        if (Array.isArray(rows)) {
          const row =
            rows.length === 1
              ? rows[0]
              : rows.find((r) => r.welcome_metric_id === mid);
          if (row) {
            const shortP = row.short_prompt ?? "";
            const mapP = row.long_map_template ?? "";
            const redP = row.long_reduce_template ?? "";
            setPromptShort(shortP);
            setPromptLongMap(mapP);
            setPromptLongReduce(redP);
            if (mapP.trim() || redP.trim()) {
              setPromptModeTab("large");
            } else {
              setPromptModeTab("small");
            }
          }
        } else if (res === false) {
          message.warning(t("welcome.promptLoadError"));
        }
      } catch (e) {
        console.error(e);
        message.error(t("welcome.promptLoadError"));
      } finally {
        setPromptLoading(false);
      }
    };

    load();
  }, [open, widget?.metric_id, uid, t]);

  const closePromptEditor = () => {
    onClose();
    setPromptShort("");
    setPromptLongMap("");
    setPromptLongReduce("");
    setPromptModeTab("small");
    setCanEditOrgPrompts(false);
  };

  const insertPromptVariable = (token) => {
    if (!canEditOrgPrompts) return;
    const mapTextArea = promptMapRef.current?.resizableTextArea?.textArea;
    const reduceTextArea = promptReduceRef.current?.resizableTextArea?.textArea;
    const smallTextArea = promptShortRef.current?.resizableTextArea?.textArea;
    const focusedElement = document.activeElement;

    let targetField = "map";
    if (promptModeTab === "small") {
      targetField = "small";
    } else if (focusedElement === reduceTextArea) {
      targetField = "reduce";
    } else if (focusedElement === mapTextArea) {
      targetField = "map";
    }

    const meta =
      targetField === "small"
        ? { value: promptShort, setValue: setPromptShort, textArea: smallTextArea }
        : targetField === "reduce"
          ? {
              value: promptLongReduce,
              setValue: setPromptLongReduce,
              textArea: reduceTextArea,
            }
          : { value: promptLongMap, setValue: setPromptLongMap, textArea: mapTextArea };

    if (typeof meta.setValue !== "function") return;
    const textArea = meta.textArea;
    const current = String(meta.value || "");
    const start = textArea?.selectionStart ?? current.length;
    const end = textArea?.selectionEnd ?? start;
    const needsLeadingSpace = start > 0 && !/\s/.test(current.charAt(start - 1));
    const insertion = `${needsLeadingSpace ? " " : ""}${token}`;
    const next = `${current.slice(0, start)}${insertion}${current.slice(end)}`;
    meta.setValue(next);
    requestAnimationFrame(() => {
      const liveRef =
        targetField === "small"
          ? promptShortRef.current?.resizableTextArea?.textArea
          : targetField === "reduce"
            ? promptReduceRef.current?.resizableTextArea?.textArea
            : promptMapRef.current?.resizableTextArea?.textArea;
      if (!liveRef || typeof liveRef.focus !== "function") return;
      const pos = start + insertion.length;
      liveRef.focus();
      liveRef.setSelectionRange(pos, pos);
    });
  };

  const downloadPromptsCsv = () => {
    if (!canEditOrgPrompts) return;
    downloadOrgLlmPromptsCsv({
      metricId: widget?.metric_id,
      widgetName: widget?.name,
      shortPrompt: promptShort,
      longMapPrompt: promptLongMap,
      longReducePrompt: promptLongReduce,
    });
    message.success(t("welcome.promptDownloadSuccess"));
  };

  const savePromptEditor = async () => {
    if (!widget?.metric_id || !canEditOrgPrompts) return;
    setPromptSaving(true);
    try {
      const res = await apiRequest(
        "welcome/organization-llm-prompts",
        "PUT",
        {
          welcome_metric_id: widget.metric_id,
          ...(uid != null && uid !== "" ? { user_id: uid } : {}),
          short_prompt: promptShort.trim() === "" ? null : promptShort,
          long_map_template: promptLongMap.trim() === "" ? null : promptLongMap,
          long_reduce_template:
            promptLongReduce.trim() === "" ? null : promptLongReduce,
        },
        true,
      );
      if (res?.success) {
        message.success(t("welcome.promptSaveSuccess"));
        closePromptEditor();
      } else if (res?.error) {
        message.error(res.error);
      } else {
        message.error(t("welcome.promptSaveError"));
      }
    } catch (e) {
      console.error(e);
      message.error(t("welcome.promptSaveError"));
    } finally {
      setPromptSaving(false);
    }
  };

  return (
    <Modal
      title={
        <span className={welcomeStyles.promptEditorModalTitle}>
          {t("welcome.editPrompt")}
          {widget?.name ? ` — ${formatNameForPromptModalChrome(widget.name)}` : ""}
        </span>
      }
      open={open}
      onCancel={closePromptEditor}
      width={1120}
      centered
      destroyOnClose
      wrapClassName={welcomeStyles.promptEditorModal}
      styles={{
        content: {
          maxHeight: "calc(100vh - 48px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
        body: {
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
        },
      }}
      footer={
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {!canEditOrgPrompts && (
            <Alert type="info" showIcon message={t("welcome.promptViewOnlyNotice")} />
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            {canEditOrgPrompts ? (
              <Button
                key="download"
                icon={<DownloadOutlined />}
                disabled={promptLoading}
                onClick={downloadPromptsCsv}
              >
                {t("welcome.downloadPrompts")}
              </Button>
            ) : null}
            <Button
              key="cancel"
              className={welcomeStyles.promptModalCancelBtn}
              onClick={closePromptEditor}
            >
              {t("common.cancel")}
            </Button>
            {canEditOrgPrompts ? (
              <Button
                key="save"
                type="primary"
                loading={promptSaving}
                onClick={savePromptEditor}
              >
                {t("common.save")}
              </Button>
            ) : null}
          </div>
        </div>
      }
    >
      <Spin spinning={promptLoading}>
        <div className={welcomeStyles.promptBuilderLayout}>
          <aside className={welcomeStyles.promptVariableSidebar}>
            <div className={welcomeStyles.promptVariableSidebarTitle}>
              <PanelLeft size={15} />
              {t("welcome.promptVarsPanelTitle")}
            </div>
            {getPromptVariableGroupsForMetric(widget?.metric_id).map((group) => (
              <div key={group.key} className={welcomeStyles.promptVariableGroup}>
                <h5>{t(group.titleKey)}</h5>
                <div className={welcomeStyles.promptVariableButtons}>
                  {group.items.map((item) => (
                    <Tooltip
                      key={item.token}
                      title={t(item.descriptionKey)}
                      placement="right"
                    >
                      <button
                        type="button"
                        className={welcomeStyles.promptVariableButton}
                        disabled={!canEditOrgPrompts}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertPromptVariable(item.token)}
                      >
                        <Braces size={13} />
                        {item.token}
                      </button>
                    </Tooltip>
                  ))}
                </div>
              </div>
            ))}
          </aside>

          <div className={welcomeStyles.promptFlowPanel}>
            <div className={welcomeStyles.promptModeTabRow}>
              <button
                type="button"
                className={`${welcomeStyles.promptModeTabButton} ${
                  promptModeTab === "small" ? welcomeStyles.promptModeTabButtonActive : ""
                }`}
                onClick={() => {
                  setPromptModeTab("small");
                }}
              >
                {t("welcome.promptModeSmallTab")}
              </button>
              <button
                type="button"
                className={`${welcomeStyles.promptModeTabButton} ${
                  promptModeTab === "large" ? welcomeStyles.promptModeTabButtonActive : ""
                }`}
                onClick={() => {
                  setPromptModeTab("large");
                }}
              >
                {t("welcome.promptModeLargeTab")}
              </button>
            </div>
            <div className={welcomeStyles.promptFlowHeader}>
              <div className={welcomeStyles.promptFlowHeaderTitle}>
                <Sparkles size={16} />
                {promptModeTab === "small"
                  ? t("welcome.promptPhaseSmallSet")
                  : t("welcome.promptFlowTitle")}
              </div>
            </div>

            <div className={welcomeStyles.promptFlowScroll}>
              {promptModeTab === "small" ? (
                <section className={welcomeStyles.promptPhaseSection}>
                  <label
                    className={welcomeStyles.promptPhaseLabel}
                    htmlFor="welcome-prompt-small-phase-ai-report"
                  >
                    <Wand2 size={15} />
                    {t("welcome.promptPhaseSmallSet")}
                  </label>
                  <Input.TextArea
                    id="welcome-prompt-small-phase-ai-report"
                    className={welcomeStyles.promptEditorTextarea}
                    value={promptShort}
                    onChange={(e) => setPromptShort(e.target.value)}
                    placeholder={t("welcome.promptPlaceholderSmall")}
                    ref={promptShortRef}
                    readOnly={!canEditOrgPrompts}
                    autoSize={{ minRows: 16, maxRows: 36 }}
                  />
                  <PromptUnknownTokensNote
                    metricId={widget?.metric_id}
                    text={promptShort}
                  />
                </section>
              ) : (
                <PromptMapReduceAccordion
                  styles={welcomeStyles}
                  metricId={widget?.metric_id}
                  mapValue={promptLongMap}
                  onMapChange={(e) => setPromptLongMap(e.target.value)}
                  reduceValue={promptLongReduce}
                  onReduceChange={(e) => setPromptLongReduce(e.target.value)}
                  mapRef={promptMapRef}
                  reduceRef={promptReduceRef}
                  canEdit={canEditOrgPrompts}
                  mapInputId="welcome-prompt-map-phase-ai-report"
                  reduceInputId="welcome-prompt-reduce-phase-ai-report"
                />
              )}
            </div>
          </div>
        </div>
      </Spin>
    </Modal>
  );
}
